import React from 'react';

import { Box, Button } from '@mui/material';
import { isNil } from 'es-toolkit';

import { Form } from '@hrlogics/react-components/form';

import {
  ConsumerNotificationSettingsLabels,
  CvAdminNotificationSettingsLabels,
  CvManagerNotificationSettingsLabels,
  EmployerAdminNotificationSettingsLabels,
  EmployerAdminUnderPeoNotificationSettingsLabels,
  EmployerAdminWithoutBillingNotificationSettingsLabels,
  EmployerManagerNotificationSettingsLabels,
  EmployerManagerUnderPeoNotificationSettingsLabels,
  PeoAdminNotificationSettingsLabels,
  PeoAdminUnderPPNotificationSettingsLabels,
  PeoManagerNotificationSettingsLabels,
  PeoManagerUnderPPNotificationSettingsLabels,
  PeoPayrollProviderNotificationSettingsLabels,
  PeoStaffWithBillingAdditionalNotificationSettingsLabels,
  VerifierStaffCommercialSettingsLabels,
  VerifierStaffGovernmentSettingsLabels,
} from '@clearverify/constants';

import { UsersNotificationSetting, VerifierType } from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import {
  useCurrentUserEmployer,
  useIsControlledByPeoOrPeoPayrollProvider,
} from 'src/hooks/employer';
import { useCurrentUserPeo } from 'src/hooks/peo';
import { useCurrentUserNotificationSettings, useCurrentUserPermissions } from 'src/hooks/user';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { SxProp } from 'src/types/mui';

import { NotificationSwitch } from './NotificationSwitch';
import { Labels, NotificationSettingsValues } from './types';

export const NotificationsSettingsForm = () => {
  const { createApolloContext } = useNotification();

  const { isMobile } = useScreenInfo();

  const { notificationSettings, isUserLoading, loading, updateNotificationSettings } =
    useCurrentUserNotificationSettings();
  const isControlledByPeoOrPeoPayrollProvider = useIsControlledByPeoOrPeoPayrollProvider();
  const { verifierType } = useCurrentUserVerifier();
  const { employer } = useCurrentUserEmployer();
  const { peo } = useCurrentUserPeo();
  const userPermissions = useCurrentUserPermissions();

  const notificationSettingsLabels = React.useMemo(() => {
    if (userPermissions.isPlatformAdmin) {
      return CvAdminNotificationSettingsLabels;
    }
    if (userPermissions.isPlatformManager) {
      return CvManagerNotificationSettingsLabels;
    }
    if (userPermissions.isEmployerAdmin) {
      if (isControlledByPeoOrPeoPayrollProvider) {
        return EmployerAdminUnderPeoNotificationSettingsLabels;
      }
      if (employer?.revenueShareSetting) {
        return EmployerAdminNotificationSettingsLabels;
      }
      return EmployerAdminWithoutBillingNotificationSettingsLabels;
    }
    if (userPermissions.isEmployerManager) {
      if (isControlledByPeoOrPeoPayrollProvider) {
        return EmployerManagerUnderPeoNotificationSettingsLabels;
      }
      return EmployerManagerNotificationSettingsLabels;
    }
    if (
      (userPermissions.isVerifierAdmin || userPermissions.isVerifierManager) &&
      verifierType === VerifierType.Commercial
    ) {
      return VerifierStaffCommercialSettingsLabels;
    }
    if (
      (userPermissions.isVerifierAdmin || userPermissions.isVerifierManager) &&
      verifierType === VerifierType.Government
    ) {
      return VerifierStaffGovernmentSettingsLabels;
    }
    if (userPermissions.isEmployee) {
      return ConsumerNotificationSettingsLabels;
    }
    if (userPermissions.isPeoSuperAdmin || userPermissions.isPeoAdmin) {
      if (!isNil(peo?.peoPayrollProvider)) {
        if (peo.revenueShareSetting) {
          return {
            ...PeoAdminUnderPPNotificationSettingsLabels,
            ...PeoStaffWithBillingAdditionalNotificationSettingsLabels,
          };
        }
        return PeoAdminUnderPPNotificationSettingsLabels;
      }
      if (peo?.revenueShareSetting) {
        return {
          ...PeoAdminNotificationSettingsLabels,
          ...PeoStaffWithBillingAdditionalNotificationSettingsLabels,
        };
      }
      return PeoAdminNotificationSettingsLabels;
    }
    if (userPermissions.isPeoManager) {
      if (!isNil(peo?.peoPayrollProvider)) {
        return PeoManagerUnderPPNotificationSettingsLabels;
      }

      return PeoManagerNotificationSettingsLabels;
    }
    if (userPermissions.isPeoPayrollProviderUser) {
      return PeoPayrollProviderNotificationSettingsLabels;
    }

    return {};
  }, [
    userPermissions.isPlatformAdmin,
    userPermissions.isPlatformManager,
    userPermissions.isEmployerAdmin,
    userPermissions.isEmployerManager,
    userPermissions.isVerifierAdmin,
    userPermissions.isVerifierManager,
    userPermissions.isEmployee,
    userPermissions.isPeoSuperAdmin,
    userPermissions.isPeoAdmin,
    userPermissions.isPeoManager,
    userPermissions.isPeoPayrollProviderUser,
    verifierType,
    isControlledByPeoOrPeoPayrollProvider,
    employer?.revenueShareSetting,
    peo?.peoPayrollProvider,
    peo?.revenueShareSetting,
  ]);

  const initialValues = React.useMemo<NotificationSettingsValues>(() => {
    return Object.keys(notificationSettingsLabels).reduce((initialValues, notificationOption) => {
      return {
        ...initialValues,
        [notificationOption]: notificationSettings.includes(
          notificationOption as UsersNotificationSetting,
        ),
      };
    }, {});
  }, [notificationSettings, notificationSettingsLabels]);

  const onSubmit = React.useCallback(
    async (formData: NotificationSettingsValues) => {
      try {
        await updateNotificationSettings(
          Object.entries(formData).reduce(
            (data, [setting, value]) =>
              value ? [...data, setting as UsersNotificationSetting] : data,
            [] as UsersNotificationSetting[],
          ),
          createApolloContext({
            successMessage: 'Updated successfully',
          }),
        );
      } catch (error) {
        console.error(error);
      }
    },
    [createApolloContext, updateNotificationSettings],
  );

  if (isUserLoading || loading) {
    return <FullscreenLoader />;
  }

  return (
    <Form<NotificationSettingsValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ dirty: true, submitting: true }}
      containerProps={{
        p: userPermissions.hasVerifierRoles && isMobile ? 3 : 0,
      }}
    >
      {({ form, dirty, submitting }) => (
        <React.Fragment>
          <Box display="flex" flexDirection="column" gap={3}>
            {Object.entries(notificationSettingsLabels).map(([setting, labels]) => (
              <NotificationSwitch key={setting} setting={setting} labels={labels as Labels} />
            ))}
          </Box>

          <Box display="flex" justifyContent="center" gap={2} mt={4}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => form.restart()}
              loading={isUserLoading || submitting}
              disabled={!dirty}
              sx={btnStyles}
            >
              Reset
            </Button>

            <Button
              type="submit"
              variant="contained"
              loading={isUserLoading || submitting}
              disabled={!dirty}
              sx={btnStyles}
            >
              Save Changes
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const btnStyles: SxProp = theme => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
});
